import { useMutation } from "@tanstack/react-query";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router";
import { useSession } from "../../app/session.js";
import { trpc, trpcErrorCode } from "../../app/trpc.js";
import { BrandMark, Button, buttonClass, Notice, Screen, Spinner, TeamIcon } from "../../ui/index.js";
import { LocaleSwitch } from "./LocaleSwitch.js";

export function InvitePage() {
  const { t } = useTranslation();
  const { token = "" } = useParams();
  const session = useSession();
  const navigate = useNavigate();
  // getInvite это mutation (POST), чтобы токен не попадал в URL запроса и логи сервера.
  const invite = useMutation(trpc.auth.getInvite.mutationOptions({ retry: false }));
  const lookup = invite.mutate;
  useEffect(() => {
    if (token) lookup({ token });
  }, [token, lookup]);
  const accept = useMutation(
    trpc.auth.acceptInvite.mutationOptions({
      onSuccess: async () => {
        await session.refresh();
        navigate("/", { replace: true });
      },
    }),
  );

  const acceptError = (err: unknown) => {
    const c = trpcErrorCode(err);
    if (c === "forbidden") return t("invite.wrongEmail");
    if (c === "conflict") return t("invite.ownerExists");
    return t("common.error");
  };

  if (invite.isPending || session.loading) return <Spinner />;
  return (
    <Screen>
      <div className="mb-6 flex items-start justify-between gap-4">
        <BrandMark className="h-10 w-10" />
        <LocaleSwitch />
      </div>
      <h1 className="mb-4 text-2xl font-semibold text-ink">{t("invite.title")}</h1>
      {invite.isError || !invite.data ? (
        <Notice kind="error">{t("invite.invalid")}</Notice>
      ) : (
        <div className="space-y-4">
          <div className="flex items-start gap-3 rounded-card border border-line bg-page p-4">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-soft text-brand">
              <TeamIcon className="h-5 w-5" />
            </span>
            <p className="text-ink">{t("invite.text", { salon: invite.data.salonName, role: t(`invite.roles.${invite.data.role}`) })}</p>
          </div>
          {session.user ? (
            <Button className="w-full" size="lg" onClick={() => accept.mutate({ token })} loading={accept.isPending}>
              {t("invite.accept")}
            </Button>
          ) : (
            <Link to={`/login?next=/invite/${token}`} className={buttonClass("primary", "w-full", "lg")}>
              {t("invite.loginFirst")}
            </Link>
          )}
          {accept.isError && <Notice kind="error">{acceptError(accept.error)}</Notice>}
        </div>
      )}
    </Screen>
  );
}
